import type { Recorded } from "../game/progress";
import type { Restaurant } from "../game/restaurants";
import { clockTime, type ShiftResult } from "../game/shift";
import { money } from "./format";
import { Stars } from "./Stars";

const VERDICTS = ["Rough day.", "You made it through.", "Solid day!", "Spotless. Customers will be back."];

export function Results({
  restaurant,
  result,
  recorded,
  onAgain,
  onBoard,
}: {
  restaurant: Restaurant;
  result: ShiftResult;
  recorded: Recorded;
  onAgain: () => void;
  onBoard: () => void;
}) {
  const goals = restaurant.setup!.goals;
  return (
    <div className="mx-auto max-w-md px-4 py-8">
      <p className="text-center text-sm font-bold uppercase tracking-wide text-accent">End of the day · {restaurant.name}</p>
      <h1 className="mt-1 text-center text-3xl font-black">{VERDICTS[result.stars]}</h1>
      <div className="my-4 text-center">
        <Stars count={result.stars} className="text-5xl" />
        {recorded.newBestStars && (
          <p className="mx-auto mt-2 w-fit rounded-full bg-sunny px-3 py-1 text-sm font-bold">New best!</p>
        )}
      </div>

      <section aria-label="Goals" className="rounded-2xl bg-panel p-4 shadow-sm">
        <ul className="space-y-2">
          {goals.map((g, i) => {
            const earned = i < result.stars;
            return (
              <li key={g.label} className="flex items-center gap-2 text-sm">
                <span aria-hidden="true" className={`w-5 text-center font-black ${earned ? "text-accent" : "text-ink-soft"}`}>
                  {earned ? "✓" : "·"}
                </span>
                <span className={earned ? "" : "text-ink-soft"}>{g.label}</span>
                <span className="sr-only">{earned ? "(earned)" : "(missed)"}</span>
              </li>
            );
          })}
        </ul>
        <dl className="mt-4 grid grid-cols-3 gap-2 border-t border-steel pt-3 text-center">
          <Stat label="Orders out" value={`${result.served}/${result.orders}`} />
          <Stat label="Walkouts" value={result.walkouts} bad={result.walkouts > 0} />
          <Stat label="Broken" value={result.breaks} bad={result.breaks > 0} />
        </dl>
        <p className="mt-2 text-center text-xs text-ink-soft">Closed up at {clockTime(restaurant.setup!, result.closedAt)}</p>
      </section>

      <section aria-label="Pay stub" className="mt-3 rounded-2xl bg-panel p-4 font-mono text-sm shadow-sm">
        <Line label="Wage" value={money(result.wage)} />
        <Line label="Tips" value={money(result.tips)} />
        {result.breakage > 0 && <Line label="Breakage" value={`−${money(result.breakage)}`} bad />}
        <div className="mt-2 flex justify-between border-t-2 border-dashed border-steel pt-2 text-base font-bold">
          <span>Take home</span>
          <span>{money(result.pay)}</span>
        </div>
        {recorded.newBestPay && <p className="mt-1 text-right font-sans text-xs font-bold text-accent">Best pay here yet</p>}
      </section>

      {recorded.unlocked.map((r) => (
        <p key={r.id} className="mt-3 rounded-2xl border-2 border-sunny bg-sunny-soft p-3 text-sm">
          <strong>Unlocked: {r.name}.</strong> {r.setup ? "It's on your list of restaurants." : "It's still being fitted out, so it opens soon."}
        </p>
      ))}

      <div className="mt-6 flex gap-2">
        <button type="button" onClick={onBoard} className="rounded-xl border-2 border-steel bg-panel px-4 py-2 font-bold hover:bg-tile">
          Restaurants
        </button>
        <button
          type="button"
          autoFocus
          onClick={onAgain}
          className="flex-1 rounded-xl bg-accent px-4 py-2 font-bold text-panel shadow hover:brightness-110"
        >
          Open up again
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, bad = false }: { label: string; value: string | number; bad?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-ink-soft">{label}</dt>
      <dd className={`text-lg font-bold ${bad ? "text-danger" : ""}`}>{value}</dd>
    </div>
  );
}

function Line({ label, value, bad = false }: { label: string; value: string; bad?: boolean }) {
  return (
    <div className={`flex justify-between ${bad ? "text-danger" : ""}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
